import { useMemo } from 'react';
import { CampaignSalesAggregate } from 'src/contexts/AdCampaignsDataContext';
import { formatCurrency, formatNumber } from 'src/utils/numberFormatter';
import {
  AdCampaignState,
  SponsoredListingsCampaign,
} from 'src/WebApiController';

import * as styles from './CampaignStatsRollup.css';

type StatsRollupValues = {
  revenue: number;
  spend: number;
  roas: number | null;
  boostedSaleCount: number;
  eventCount: number;
  pausedCampaignCount: number;
  totalCampaignCount: number;
  margin: number | null;
};

export function computeStatsRollup(
  allCampaigns: SponsoredListingsCampaign[] | null,
  campaignSalesAggregates:
    | Record<number, CampaignSalesAggregate>
    | null
    | undefined
): StatsRollupValues {
  if (!allCampaigns || allCampaigns.length === 0) {
    return {
      revenue: 0,
      spend: 0,
      roas: null,
      boostedSaleCount: 0,
      eventCount: 0,
      pausedCampaignCount: 0,
      totalCampaignCount: 0,
      margin: null,
    };
  }

  let totalRevenue = 0;
  let totalSpend = 0;
  let totalAdSpend = 0;
  let totalSaleCount = 0;
  const uniqueEventIds = new Set<number>();

  allCampaigns.forEach((campaign) => {
    uniqueEventIds.add(campaign.eventId);
    totalAdSpend += campaign.totalSpend ?? 0;
    const aggregate = campaignSalesAggregates?.[campaign.campaignId];
    if (aggregate) {
      totalRevenue += aggregate.totalNetProceeds;
      totalSpend += aggregate.totalCost;
      totalSaleCount += aggregate.saleCount;
    }
  });

  const pausedCampaignCount = allCampaigns.filter(
    (c) => c.campaignState === AdCampaignState.Paused
  ).length;
  const totalCampaignCount = allCampaigns.length;
  const roas = totalSpend > 0 ? totalRevenue / totalSpend : null;
  const margin =
    totalRevenue > 0
      ? (totalRevenue - (totalSpend + totalAdSpend)) / totalRevenue
      : null;

  return {
    revenue: totalRevenue,
    spend: totalSpend,
    roas,
    boostedSaleCount: totalSaleCount,
    eventCount: uniqueEventIds.size,
    pausedCampaignCount,
    totalCampaignCount,
    margin,
  };
}

export function formatCompactCurrency(
  value: number,
  currencyCode = 'USD'
): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000) {
    return `${sign}${formatCurrency(absValue / 1_000_000, currencyCode, 2)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}${formatCurrency(absValue / 1_000, currencyCode, 1)}K`;
  }
  return `${sign}${formatCurrency(absValue, currencyCode, 0)}`;
}

type StatCardProps = {
  title: string;
  value: string;
  secondaryValue?: string;
  accent?: boolean;
};

function StatCard({ title, value, secondaryValue, accent }: StatCardProps) {
  return (
    <div className={`${styles.card}${accent ? ` ${styles.cardAccent}` : ''}`}>
      <div className={styles.cardTitle}>{title}</div>
      <div className={styles.cardValueRow}>
        <div className={styles.cardValue}>{value}</div>
        {secondaryValue && (
          <div className={styles.cardSecondaryValue}>{secondaryValue}</div>
        )}
      </div>
    </div>
  );
}

type CampaignStatsRollupProps = {
  allCampaigns: SponsoredListingsCampaign[] | null;
  campaignSalesAggregates:
    | Record<number, CampaignSalesAggregate>
    | null
    | undefined;
  currencyCode?: string;
};

export function CampaignStatsRollup({
  allCampaigns,
  campaignSalesAggregates,
  currencyCode = 'USD',
}: CampaignStatsRollupProps) {
  const stats = useMemo(
    () => computeStatsRollup(allCampaigns, campaignSalesAggregates),
    [allCampaigns, campaignSalesAggregates]
  );

  const roasDisplay =
    stats.roas != null ? `${formatNumber(stats.roas * 100, 0)}%` : '—';

  const pausedPercentDisplay =
    stats.totalCampaignCount > 0
      ? `${formatNumber((stats.pausedCampaignCount / stats.totalCampaignCount) * 100, 0)}%`
      : '0%';

  return (
    <div className={styles.cardsRow}>
      <StatCard
        title="Revenue"
        value={formatCompactCurrency(stats.revenue, currencyCode)}
        accent
      />
      <StatCard
        title="Spend"
        value={formatCompactCurrency(stats.spend, currencyCode)}
      />
      <StatCard title="ROAS" value={roasDisplay} />
      <StatCard
        title="Boosted Listings"
        value={formatNumber(stats.boostedSaleCount)}
        secondaryValue={`${formatNumber(stats.eventCount)} ${stats.eventCount === 1 ? 'Event' : 'Events'}`}
      />
      <StatCard
        title="Paused Campaigns"
        value={formatNumber(stats.pausedCampaignCount)}
        secondaryValue={pausedPercentDisplay}
      />
    </div>
  );
}
